import { useState, useEffect, useCallback } from "react";
import { DEFAULT_DASHBOARD_LAYOUT } from "./defaultLayout";
import { WIDGET_REGISTRY } from "./widgetRegistry";
import { arrayMove } from "@dnd-kit/sortable";

const STORAGE_KEY = "PMTX_DASHBOARD_LAYOUT_V14";

export const useDashboardLayout = () => {
  const [layout, setLayout] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const savedMap = new Map(parsed.map((item) => [item.id, item]));
          const merged = DEFAULT_DASHBOARD_LAYOUT.map((def) => {
            if (savedMap.has(def.id)) {
              return { ...def, ...savedMap.get(def.id) };
            }
            return { ...def };
          });
          parsed.forEach((item) => {
            if (!merged.some((m) => m.id === item.id) && WIDGET_REGISTRY[item.id]) {
              merged.push({ ...item });
            }
          });
          return merged;
        }
      }
    } catch (e) {
      console.warn("Failed to load dashboard layout from localStorage:", e);
    }
    return DEFAULT_DASHBOARD_LAYOUT.map((def) => ({ ...def }));
  });

  const [isCustomizing, setIsCustomizing] = useState(false);
  const [activeWidgetId, setActiveWidgetId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const saveLayout = useCallback((newLayout = layout) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newLayout));
      setHasUnsavedChanges(false);
    } catch (e) {
      console.error("Failed to save dashboard layout:", e);
    }
  }, [layout]);

  const resetToDefault = useCallback(() => {
    const freshDefault = DEFAULT_DASHBOARD_LAYOUT.map((item) => ({ ...item }));
    setLayout(freshDefault);
    try {
      for (let i = 1; i <= 10; i++) {
        localStorage.removeItem(`PMTX_DASHBOARD_LAYOUT_V${i}`);
      }
    } catch (e) {}
    setHasUnsavedChanges(false);
  }, []);

  const updateWidgetColSpan = useCallback((id, colSpan) => {
    setLayout((prev) => {
      const updated = prev.map((w) => (w.id === id ? { ...w, colSpan } : w));
      setHasUnsavedChanges(true);
      return updated;
    });
  }, []);

  const updateWidgetHeight = useCallback((id, rowHeight) => {
    setLayout((prev) => {
      const updated = prev.map((w) => (w.id === id ? { ...w, rowHeight } : w));
      setHasUnsavedChanges(true);
      return updated;
    });
  }, []);

  const alignRowHeights = useCallback(() => {
    setLayout((prev) => {
      let currentSpanSum = 0;
      let currentRowWidgets = [];
      const updated = prev.map((item) => ({ ...item }));

      const applyRowMaxHeight = (rowWidgets) => {
        if (rowWidgets.length === 0) return;
        const maxHeight = Math.max(...rowWidgets.map((w) => w.rowHeight || 220));
        rowWidgets.forEach((w) => {
          const item = updated.find((u) => u.id === w.id);
          if (item) item.rowHeight = maxHeight;
        });
      };

      prev.forEach((w) => {
        if (w.hidden) return;
        const span = w.colSpan || 12;
        if (currentSpanSum + span > 12) {
          applyRowMaxHeight(currentRowWidgets);
          currentSpanSum = span;
          currentRowWidgets = [w];
        } else {
          currentSpanSum += span;
          currentRowWidgets.push(w);
        }
      });
      applyRowMaxHeight(currentRowWidgets);

      setHasUnsavedChanges(true);
      return updated;
    });
  }, []);

  const toggleWidgetHidden = useCallback((id) => {
    setLayout((prev) => {
      const updated = prev.map((w) => (w.id === id ? { ...w, hidden: !w.hidden } : w));
      setHasUnsavedChanges(true);
      return updated;
    });
  }, []);

  const reorderWidgets = useCallback((activeId, overId) => {
    if (activeId === overId) return;
    setLayout((prev) => {
      const oldIndex = prev.findIndex((w) => w.id === activeId);
      const newIndex = prev.findIndex((w) => w.id === overId);
      if (oldIndex !== -1 && newIndex !== -1) {
        const updated = arrayMove(prev, oldIndex, newIndex);
        setHasUnsavedChanges(true);
        return updated;
      }
      return prev;
    });
  }, []);

  return {
    layout,
    isCustomizing,
    setIsCustomizing,
    activeWidgetId,
    setActiveWidgetId,
    hasUnsavedChanges,
    updateWidgetColSpan,
    updateWidgetHeight,
    alignRowHeights,
    toggleWidgetHidden,
    reorderWidgets,
    resetToDefault,
    saveLayout,
  };
};
